'use client';

import { Progress } from '@curology/ui-components-web/react/Progress';
import { atom, useAtom } from 'jotai';
import React, { useEffect, useMemo } from 'react';

import { FlashMessagesState } from '@/atoms/flashMessages/flashMessagesState';
import { useDataProvider } from '@/components/flow/components/DataProvider/useDataProvider';
import { useFlowContext } from '@/components/flow/hooks/useFlowContext';
import { useCheckout } from '@/components/flow/steps/checkout/hooks/useCheckout';
import { CHECKOUT_MODES } from '@/components/flow/steps/checkout/types';
import { useStripeClient } from '@/components/payments/hooks/useStripeClient';
import { DATA_KEYS } from '@/consts/dataKeys';
import { ApplePayService } from '@/services/productSelectionAndCheckoutService/applePay';
import { ProductSelectionAndCheckoutService } from '@/services/productSelectionAndCheckoutService/client';

import { useCheckoutMode } from '../../../hooks/useCheckoutMode';
import { useSelectedItemIds } from '../../../hooks/useSelectedItemIds';
import { useTax } from '../../../hooks/useTax';
import { useTotals } from '../../../hooks/useTotals';
import {
  applePayButtonContainerId,
  renderApplePayButton,
} from '../../../js/applePay';

export const canUseApplePayAtom = atom(false);

const ApplePayButton = ({
  showFlashMessageOnCheckoutSuccess,
}: {
  showFlashMessageOnCheckoutSuccess?: boolean;
}) => {
  const [canUseApplePay, setCanUseApplePay] = useAtom(canUseApplePayAtom);
  const { brand } = useFlowContext();
  const checkoutMode = useCheckoutMode();
  const { flags, handling, patient, shipping } = useDataProvider(
    DATA_KEYS.productSelection,
  );
  const { tax, setTax } = useTax();
  const {
    futureShipmentTotal,
    trialDiscountTotal,
    trialTotalWithoutDiscounts,
    trialTotalWithTax,
  } = useTotals();
  const stripeClient = useStripeClient();
  const { selectedItemIds } = useSelectedItemIds();
  const { onSuccess, onIneligible } = useCheckout();
  const { treatmentCategory } = useFlowContext();
  const [flashMessages, setFlashMessages] = useAtom(
    FlashMessagesState.flashMessagesAtom,
  );

  const onSubmit = useMemo(
    () =>
      checkoutMode === CHECKOUT_MODES.Trial
        ? ApplePayService.activateExpressCheckoutTrial
        : ApplePayService.activateExpressCheckoutSubscription,
    [checkoutMode],
  );

  useEffect(() => {
    if (!stripeClient) {
      return;
    }

    renderApplePayButton(
      brand,
      stripeClient,
      flags,
      flashMessages,
      futureShipmentTotal,
      handling,
      patient,
      selectedItemIds,
      setCanUseApplePay,
      setFlashMessages,
      shipping,
      tax,
      trialDiscountTotal,
      trialTotalWithoutDiscounts,
      trialTotalWithTax,
      onSubmit,
      treatmentCategory,
      showFlashMessageOnCheckoutSuccess,
      onSuccess,
      onIneligible,
    );
  }, [
    brand,
    flashMessages,
    futureShipmentTotal,
    handling,
    patient,
    selectedItemIds,
    setCanUseApplePay,
    setFlashMessages,
    shipping,
    showFlashMessageOnCheckoutSuccess,
    tax,
    trialDiscountTotal,
    trialTotalWithoutDiscounts,
    trialTotalWithTax,
    flags,
    treatmentCategory,
    onSuccess,
    onIneligible,
    stripeClient,
    checkoutMode,
    onSubmit,
  ]);

  useEffect(() => {
    if (!canUseApplePay) {
      return;
    }
    (async () => {
      const {
        data: { tax },
      } =
        await ProductSelectionAndCheckoutService.getUpdatedProductSelectionData(
          brand,
          treatmentCategory,
          selectedItemIds,
        );

      setTax(tax);
    })();
  }, [brand, selectedItemIds, canUseApplePay, treatmentCategory, setTax]);

  return (
    <div className="cur-text-center" id={applePayButtonContainerId}>
      {!stripeClient && (
        <Progress
          className="cur-inline-block"
          progressType="circle"
          state="highlight"
        />
      )}
      {stripeClient && <div id="apple-pay-button" />}
    </div>
  );
};

export default ApplePayButton;
